import { QueryClient } from "@tanstack/react-query";
import { serverApi } from "../api/server";
import { isApiError } from "../../types/api";

export interface ServerQueryOptions {
  cartId?: string;
  revalidate?: number;
  tags?: string[];
  cache?: RequestCache;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: string;
  headers?: Record<string, string>;
  /** Expected failure statuses (e.g. 404) — skip error logging. */
  ignoreStatusCodes?: number[];
}

// Generic prefetch function
export async function prefetchQuery<T extends object>(
  queryClient: QueryClient,
  queryKey: string[],
  endpoint: string,
  options: ServerQueryOptions = {}
): Promise<{ data: T | null; error: string | null }> {
  try {
    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: async () => {
        let response;

        if (options.method === "POST" || options.method === "PUT") {
          response = await serverApi.post<T>(
            endpoint,
            options.body ? JSON.parse(options.body) : {},
            options
          );
        } else {
          response = await serverApi.get<T>(endpoint, options);
        }

        if (isApiError(response)) {
          console.error(`Failed to fetch ${endpoint}:`, response.error);
          throw new Error(response.error?.message || "Unknown error");
        }

        // Return the data property from the success response
        return response;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
    return { data: data.data, error: null };
  } catch (error) {
    console.error(`Failed to prefetch ${endpoint}:`, error);
    const errorMessage =
      error instanceof Error ? error.message : "Network error";
    return { data: null, error: errorMessage };
  }
}

export async function fetchQuery<T extends object>(
  endpoint: string,
  options: ServerQueryOptions = {}
): Promise<{ data: T | null; error: string | null }> {
  try {
    let response;

    if (options.method === "POST" || options.method === "PUT") {
      response = await serverApi.post<T>(
        endpoint,
        options.body ? JSON.parse(options.body) : {},
        options
      );
    } else {
      response = await serverApi.get<T>(endpoint, options);
    }

    if (isApiError(response)) {
      const error = response.error?.message || "Unknown error";
      const statusCode = response.error?.statusCode;
      const isIgnoredStatus =
        statusCode !== undefined &&
        (options.ignoreStatusCodes?.includes(statusCode) ?? false);

      if (!isIgnoredStatus) {
        console.error(`Failed to fetch ${endpoint}: ${error}`);
      }
      return { data: null, error };
    }

    // Return the data property from the success response
    return { data: response.data, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Network error";
    console.error(`Failed to fetch ${endpoint}:`, error);
    return { data: null, error: errorMessage };
  }
}
